/**
 * Excel Formula Evaluation Engine for 247DevToolsHub
 * Supports:
 * - Basic mathematical expressions: +, -, *, /, ()
 * - Cell references: A1, B4, AA12 etc.
 * - Cell ranges: A1:B3, C1:C10
 * - Excel Functions: SUM, AVERAGE, MIN, MAX, COUNT, CONCAT, UPPER, LOWER, IF
 * - Error detection: #REF!, #VALUE!, #CIRCULAR!, #ERROR!
 */

// Convert 0-indexed column number to Excel column letters (A, B, C... Z, AA, AB...)
export function getColLetter(index: number): string {
  let temp = index;
  let letter = "";
  while (temp >= 0) {
    letter = String.fromCharCode((temp % 26) + 65) + letter;
    temp = Math.floor(temp / 26) - 1;
  }
  return letter;
}

// Convert Excel column letters (A, B, AA...) to 0-indexed column index
export function letterToCol(letter: string): number {
  let column = 0;
  const length = letter.length;
  for (let i = 0; i < length; i++) {
    column += (letter.charCodeAt(i) - 64) * Math.pow(26, length - i - 1);
  }
  return column - 1;
}

// Parse a cell reference string like "A1" or "C12" into { row, col }
export function parseCellRef(ref: string): { row: number; col: number } | null {
  const match = ref.toUpperCase().match(/^([A-Z]+)([0-9]+)$/);
  if (!match) return null;
  const colLetter = match[1];
  const rowNum = parseInt(match[2], 10);
  return {
    row: rowNum - 1,
    col: letterToCol(colLetter),
  };
}

// Check if a string is a cell reference
export function isCellRef(str: string): boolean {
  return /^[A-Z]+[0-9]+$/i.test(str.trim());
}

// Get all cell coordinates in a range, e.g. "A1:B3"
export function getCellsInRange(rangeStr: string): { row: number; col: number }[] {
  const parts = rangeStr.split(":");
  if (parts.length !== 2) return [];
  const start = parseCellRef(parts[0]);
  const end = parseCellRef(parts[1]);
  if (!start || !end) return [];

  const cells: { row: number; col: number }[] = [];
  const minRow = Math.min(start.row, end.row);
  const maxRow = Math.max(start.row, end.row);
  const minCol = Math.min(start.col, end.col);
  const maxCol = Math.max(start.col, end.col);

  for (let r = minRow; r <= maxRow; r++) {
    for (let c = minCol; c <= maxCol; c++) {
      cells.push({ row: r, col: c });
    }
  }
  return cells;
}

/**
 * Formula Evaluator Class
 */
export class FormulaEvaluator {
  private rows: string[][];
  private headers: string[];
  private memo: Map<string, string>;
  private evaluating: Set<string>;

  constructor(rows: string[][], headers: string[]) {
    this.rows = rows;
    this.headers = headers;
    this.memo = new Map<string, string>();
    this.evaluating = new Set<string>();
  }

  // Get cell value raw
  private getRawValue(row: number, col: number): string {
    if (row < 0 || row >= this.rows.length || col < 0 || col >= this.headers.length) {
      return "";
    }
    return this.rows[row][col] || "";
  }

  // Resolve cell value (either raw or evaluated formula)
  public evaluateCell(row: number, col: number): string {
    const cellKey = `${getColLetter(col)}${row + 1}`;
    
    // Check memo cache first
    if (this.memo.has(cellKey)) {
      return this.memo.get(cellKey)!;
    }

    // Circular Dependency Guard
    if (this.evaluating.has(cellKey)) {
      return "#CIRCULAR!";
    }

    const rawVal = this.getRawValue(row, col).trim();
    if (!rawVal.startsWith("=")) {
      return rawVal; // Static value
    }

    this.evaluating.add(cellKey);
    let result = "";
    try {
      result = this.evaluateExpression(rawVal.substring(1), row, col);
    } catch (e) {
      result = "#ERROR!";
    }
    this.evaluating.delete(cellKey);

    this.memo.set(cellKey, result);
    return result;
  }

  // Evaluates a formulas expression with robust nested functions and arithmetic operators
  public evaluateExpression(expr: string, ctxRow: number, ctxCol: number): string {
    let currentExpr = expr.trim();
    
    let iterations = 0;
    const maxIterations = 100; // Allow deeper nesting

    while (iterations < maxIterations) {
      iterations++;
      
      const funcMatch = this.findFirstFunctionCall(currentExpr);
      if (!funcMatch) {
        break; // No more Excel functions found
      }

      const { name, startIdx, openParenIdx } = funcMatch;
      
      // Find matching closing parenthesis
      let bracketCount = 1;
      let closeParenIdx = -1;
      for (let i = openParenIdx + 1; i < currentExpr.length; i++) {
        if (currentExpr[i] === '(') {
          bracketCount++;
        } else if (currentExpr[i] === ')') {
          bracketCount--;
          if (bracketCount === 0) {
            closeParenIdx = i;
            break;
          }
        }
      }

      if (closeParenIdx === -1) {
        break; // Unbalanced parenthesis
      }

      const argsStr = currentExpr.substring(openParenIdx + 1, closeParenIdx);
      
      // Compute the evaluated function value recursively
      const evaluatedFuncVal = this.executeFunction(name, argsStr, ctxRow, ctxCol);

      // Reassemble the expression, replacing this function call
      currentExpr = currentExpr.substring(0, startIdx) + evaluatedFuncVal + currentExpr.substring(closeParenIdx + 1);
    }

    // After function replacements, replace all remaining standalone cell references
    currentExpr = this.resolveCellReferences(currentExpr);

    // Run the safe math and comparison evaluator
    return this.safeMathEval(currentExpr);
  }

  // Locates the first Excel function (e.g., SUM(, IF() at any position
  private findFirstFunctionCall(expr: string) {
    const funcNames = ["SUM", "AVERAGE", "MIN", "MAX", "COUNT", "CONCAT", "UPPER", "LOWER", "IF"];
    let earliestIdx = -1;
    let detectedName = "";
    let detectedOpenParenIdx = -1;

    for (const name of funcNames) {
      const regex = new RegExp(`\\b(${name})\\s*\\(`, "i");
      const match = expr.match(regex);
      if (match && match.index !== undefined) {
        if (earliestIdx === -1 || match.index < earliestIdx) {
          earliestIdx = match.index;
          detectedName = match[1];
          detectedOpenParenIdx = match.index + match[0].indexOf('(');
        }
      }
    }

    if (earliestIdx !== -1) {
      return {
        name: detectedName,
        startIdx: earliestIdx,
        openParenIdx: detectedOpenParenIdx
      };
    }
    return null;
  }

  // Executes an Excel function by resolving and passing arguments
  private executeFunction(name: string, argsStr: string, ctxRow: number, ctxCol: number): string {
    const argsList = this.splitArgs(argsStr);
    const evaluatedArgs: string[] = [];

    for (const arg of argsList) {
      const trimmedArg = arg.trim();
      
      if (/^[A-Z]+[0-9]+:[A-Z]+[0-9]+$/i.test(trimmedArg)) {
        // Range: expand to coordinate values
        const rangeCells = getCellsInRange(trimmedArg);
        for (const cell of rangeCells) {
          evaluatedArgs.push(this.evaluateCell(cell.row, cell.col));
        }
      } else if (isCellRef(trimmedArg)) {
        // Single cell
        const ref = parseCellRef(trimmedArg);
        if (ref) {
          evaluatedArgs.push(this.evaluateCell(ref.row, ref.col));
        } else {
          evaluatedArgs.push("0");
        }
      } else {
        // Non-range, non-single-cell: evaluate as expression recursively
        evaluatedArgs.push(this.evaluateExpression(trimmedArg, ctxRow, ctxCol));
      }
    }

    const funcName = name.toUpperCase();
    switch (funcName) {
      case "SUM": {
        const sum = evaluatedArgs.reduce((acc, val) => {
          const num = Number(val);
          return acc + (isNaN(num) ? 0 : num);
        }, 0);
        return String(sum);
      }
      case "AVERAGE": {
        const nums = evaluatedArgs.map(Number).filter(n => !isNaN(n));
        return nums.length > 0 ? String(nums.reduce((a, b) => a + b, 0) / nums.length) : "0";
      }
      case "MIN": {
        const nums = evaluatedArgs.map(Number).filter(n => !isNaN(n));
        return nums.length > 0 ? String(Math.min(...nums)) : "0";
      }
      case "MAX": {
        const nums = evaluatedArgs.map(Number).filter(n => !isNaN(n));
        return nums.length > 0 ? String(Math.max(...nums)) : "0";
      }
      case "COUNT": {
        const count = evaluatedArgs.filter(val => val !== "" && !isNaN(Number(val))).length;
        return String(count);
      }
      case "CONCAT": {
        return evaluatedArgs.join("").replace(/^"|"$/g, "");
      }
      case "UPPER": {
        return evaluatedArgs.join("").toUpperCase().replace(/^"|"$/g, "");
      }
      case "LOWER": {
        return evaluatedArgs.join("").toLowerCase().replace(/^"|"$/g, "");
      }
      case "IF": {
        const cond = evaluatedArgs[0];
        const isTrue = this.evaluateCondition(cond);
        return isTrue ? (evaluatedArgs[1] || "") : (evaluatedArgs[2] || "");
      }
      default:
        return "0";
    }
  }

  // Evaluates logic conditions supporting comparison operators (<, >, <=, >=, ==, !=, ===, !==)
  private evaluateCondition(cond: string): boolean {
    const trimmed = cond.trim();
    if (!trimmed) return false;
    if (trimmed === "true" || trimmed === '"true"') return true;
    if (trimmed === "false" || trimmed === '"false"') return false;

    try {
      let cleanCond = trimmed;
      // Convert single "=" into "==" for safe execution, skipping already formed logical symbols
      cleanCond = cleanCond.replace(/(?<![<>=!])=(?![=])/g, "==");
      
      // Permit only safe operators and elements to protect browser runtime
      if (/^[a-zA-Z0-9+\-*/().\s"'<>=!&|]+$/.test(cleanCond)) {
        const result = Function(`"use strict"; return (${cleanCond})`)();
        return !!result;
      }
    } catch {
      // fallback
    }

    const num = Number(trimmed);
    return !isNaN(num) ? num !== 0 : trimmed !== "" && trimmed !== "false" && !trimmed.startsWith("#");
  }

  // Replaces standalone A1 references with live cell values (ignores references placed inside text quotes)
  private resolveCellReferences(expr: string): string {
    const regex = /"[^"\\]*(?:\\.[^"\\]*)*"|\b([A-Z]+[0-9]+)\b/gi;
    return expr.replace(regex, (match, cellRef) => {
      if (cellRef) {
        const ref = parseCellRef(cellRef);
        if (!ref) return "0";
        const val = this.evaluateCell(ref.row, ref.col);
        if (val === "#CIRCULAR!") throw new Error("Circular Reference");
        if (val === "#REF!") return "0";
        if (val === "") return "0";
        if (!isNaN(Number(val))) return val;
        // String result, serialize to JSON representation for safe JS eval
        return JSON.stringify(val);
      }
      return match;
    });
  }

  // Separates comma arguments, ignoring commas nestled inside quotes or brackets
  private splitArgs(argsStr: string): string[] {
    const args: string[] = [];
    let currentArg = "";
    let inQuotes = false;
    let bracketCount = 0;

    for (let i = 0; i < argsStr.length; i++) {
      const char = argsStr[i];
      if (char === '"') {
        inQuotes = !inQuotes;
        currentArg += char;
      } else if (char === '(' && !inQuotes) {
        bracketCount++;
        currentArg += char;
      } else if (char === ')' && !inQuotes) {
        bracketCount--;
        currentArg += char;
      } else if (char === ',' && !inQuotes && bracketCount === 0) {
        args.push(currentArg);
        currentArg = "";
      } else {
        currentArg += char;
      }
    }
    args.push(currentArg);
    return args;
  }

  // Evaluates clean arithmetic/logical expressions with strict operator safety
  private safeMathEval(expr: string): string {
    const cleanExpr = expr.trim();
    if (!cleanExpr) return "0";

    // If string remains quoted, strip off outer quotes
    if (cleanExpr.startsWith('"') && cleanExpr.endsWith('"')) {
      return cleanExpr.slice(1, -1);
    }
    
    if (!isNaN(Number(cleanExpr))) {
      return cleanExpr;
    }

    try {
      // Validate safe math/logic characters
      if (/^[0-9+\-*/().\s<>!=&|]+$/.test(cleanExpr)) {
        const value = Function(`"use strict"; return (${cleanExpr})`)();
        return value !== undefined ? String(value) : "0";
      }
    } catch {
      return "#VALUE!";
    }
    
    return cleanExpr;
  }
}
